import axios from 'axios';
import * as _ from 'lodash';
import atypes from './actionTypes';
import {
    ociConnection, updateQueryStringParam, getAuth0UserProfilePhone, getAuth0UserProfileCountryCode,
    isServiceCmUri, getQueryStringParam, validateUiMode,
    verifyNetAppDomain, validateEncodeRedirectPath, getAuthorizedHeader 
} from '../common/utils';
import {
    REST_API,
    OCI_SSO,
    CS_PRODUCT,
    REPORTING_APP_ID,
    UI_MODE,
} from '../common/constants';
import {useServerLog} from '../components/error/utils';


export const getConfig = () => (dispatch) => {
    try {
        dispatch({type: atypes.LOAD_CONFIG_REQUEST});
        axios
            .get(REST_API + '/systemInfo')
            .then((res) => {
                dispatch({type: atypes.LOAD_CONFIG_SUCCESS, payload: res.data});
            })
            .catch(function(err) {
                dispatch({type: atypes.LOAD_CONFIG_FAILURE, payload: err});
            });
    }
    catch (error) {
        dispatch({type: atypes.LOAD_CONFIG_FAILURE, payload: error});
    }
};

export const setTenantLocation = (locationObj) => (dispatch) => {
    if ( _.isNil(locationObj) || _.isEmpty(locationObj)) {
        const errMessage = 'Failed to set Data Infrastructure Insights location.';
        dispatch({type: atypes.SET_TENANT_LOCATION_FAILURE, payload: errMessage});
    }
    else {
        dispatch({type: atypes.SET_TENANT_LOCATION, payload: locationObj});
    }
};

export const getUserProfile = (auth, cb) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        if (!cb) {
            dispatch({type: atypes.LOAD_USER_PROFILE_REQUEST});
        }
        axios
            .get(REST_API + '/user/profile', { headers })
            .then((res) => {
                if (cb) {
                    cb(null, res.data);
                }
                else {
                    dispatch({type: atypes.LOAD_USER_PROFILE_SUCCESS, payload: res.data});
                }
            })
            .catch(function(err) {
                dispatch({type: atypes.LOAD_USER_PROFILE_FAILURE, payload: err});
            });
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
};

export const getTenants = (auth) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        dispatch({type: atypes.LOAD_OCI_INSTANCES_REQUEST});
        axios
            .get(REST_API + '/tenants', { headers })
            .then((res) => {
                dispatch({type: atypes.LOAD_OCI_INSTANCES_SUCCESS, payload: res.data});
            })
            .catch(function(err) {
                dispatch({type: atypes.LOAD_OCI_INSTANCES_FAILURE, payload: err});
            });
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
};

export const getUserData = (auth, edition) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        let userApi = '/user';
        if (edition === true) {
            userApi = '/user/?edition=true';
        }
        dispatch({type: atypes.LOAD_USER_DATA_REQUEST});
        axios
            .get(REST_API + userApi, { headers })
            .then((res) => {
                dispatch({type: atypes.LOAD_USER_DATA_SUCCESS, payload: res.data});
            })
            .catch(function(err) {
                dispatch({type: atypes.LOAD_USER_DATA_FAILURE, payload: err});
            });
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
};

export const getUserDataAndLocations = (auth) => (dispatch) => {
    const headers = getAuthorizedHeader(auth);
    let userApi = '/user';
    if (isServiceCmUri()) {
        let tenantTypeMode = getQueryStringParam(UI_MODE.KEY);
        if (!_.isEmpty(tenantTypeMode)) {
            tenantTypeMode = _.toUpper(tenantTypeMode);
            if (tenantTypeMode === UI_MODE.CM_MODE || tenantTypeMode === UI_MODE.TENANT_TYPE_CI ||
                tenantTypeMode === UI_MODE.TENANT_TYPE_ONTAP || tenantTypeMode === UI_MODE.TENANT_TYPE_CS) {
                // For now, set mode to CM only until WebUI support mode = CS and ONTAP.
                userApi = '/user/?edition=true&mode=CM';
            }
            else {
                userApi = '/user/?edition=true&mode=' + _.trim(tenantTypeMode);
            }
        }
        else {
            // If mode is missing in query parameter, set default to CM.
            userApi = '/user/?edition=true&mode=CM';
        }
    }

    dispatch({type: atypes.LOAD_USER_AND_LOCATIONS_REQUEST});

    axios.all([
        axios.get(REST_API + userApi, { headers }),
        axios.get(REST_API + '/locations', { headers }),
    ]).then(axios.spread( (userData, locations) => {
        dispatch({type: atypes.LOAD_USER_AND_LOCATIONS_SUCCESS, userData: userData.data, locations: locations.data.results});
    })).catch((error) => {
        dispatch({type: atypes.LOAD_USER_AND_LOCATIONS_FAILURE, payload: error});
    });
};

export const getTenantsByStatus = (auth, status) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        dispatch({type: atypes.LOAD_OCI_INSTANCES_REQUEST});
        axios
            .get(REST_API + '/tenants?status=' + status, { headers })
            .then((res) => {
                dispatch({type: atypes.LOAD_OCI_INSTANCES_SUCCESS, payload: res.data});
            })
            .catch(function(err) {
                window.CIG_LOG.debug('Gateway: Action get tenants by status error: ', err);
                //dispatch({type: atypes.LOAD_OCI_INSTANCES_FAILURE, payload: err});
            })
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
};

export const getTenantCreationStatus = (auth, id) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        dispatch({type: atypes.GET_TENANT_STATUS_REQUEST});
        axios
            .get(REST_API + '/tenants/' + id, { headers })
            .then((res) => {
                dispatch({type: atypes.GET_TENANT_STATUS_SUCCESS, payload: res.data});
            })
            .catch(function(err) {
                dispatch({type: atypes.GET_TENANT_STATUS_FAILURE, payload: err});
            });
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
};

const prepareCreateTenantData = (domain, registrationInfo, tenantLocation) => {
    let data = {
        subDomain: domain,
    };

    if (!_.isEmpty(registrationInfo)) {
        data.registrationInfo = registrationInfo;
    }
    data.location = {};
    if (!_.isEmpty(tenantLocation)) {
        data.location = {
            cloud: tenantLocation.cloud,
            region: tenantLocation.region,
        };
    }
    return data;
};

export const createTenant = (auth, domain, registrationInfo, auth0UserProfile, marketingData={}, tenantLocation= {}) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        dispatch({type: atypes.CREATE_OCI_INSTANCES_REQUEST});
        let data = prepareCreateTenantData(domain, registrationInfo, tenantLocation);
        axios
            .post(REST_API + '/tenant', data, { headers })
            .then((res) => {
                dispatch({type: atypes.CREATE_OCI_INSTANCES_SUCCESS, payload: res.data})
            })
            .catch(function(err) {
                if (!_.isUndefined(err.response.data)) {
                    const respData = err.response.data;
                    if (respData.httpStatus === 400 && respData.errorCode === 1121) {
                        const phoneNumber = getAuth0UserProfilePhone(auth0UserProfile);
                        const countryCode = getAuth0UserProfileCountryCode(auth0UserProfile);

                        const marketingOptIn = marketingData.marketingOptIn === 1 || marketingData.marketingOptIn === '1' ||
                            marketingData.marketingOptIn === true || marketingData.marketingOptIn === 'true';

                        let fakeRegistrationForm = {
                            'phoneNumber': phoneNumber,
                            'countryCode': countryCode,

                            ...marketingData,
                            'marketingOptIn': marketingOptIn
                        };

                        data = prepareCreateTenantData(domain, fakeRegistrationForm, tenantLocation);
                        axios
                            .post(REST_API + '/tenant', data, { headers })
                            .then((res) => {
                                dispatch({type: atypes.CREATE_OCI_INSTANCES_SUCCESS, payload: res.data})
                            })
                            .catch(function (err) {
                                dispatch({type: atypes.CREATE_OCI_INSTANCES_FAILURE, payload: err});
                            });
                    }
                    else {
                        dispatch({type: atypes.CREATE_OCI_INSTANCES_FAILURE, payload: err});
                    }
                }
            });
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
}

export const createTenantIfNotExisted = (auth, domain) => (dispatch) => {
    try {
        const headers = getAuthorizedHeader(auth);
        dispatch({type: atypes.LOAD_OCI_INSTANCES_REQUEST});
        axios
            .get(REST_API + '/tenants', { headers })
            .then((result) => {
                dispatch({type: atypes.LOAD_OCI_INSTANCES_SUCCESS, payload: result.data});
                if (result.data.length === 0) {
                    dispatch({type: atypes.CREATE_OCI_INSTANCES_REQUEST});
                    axios
                        .post(REST_API + '/tenant', { subDomain: domain }, { headers })
                        .then((res) => {
                            dispatch({type: atypes.CREATE_OCI_INSTANCES_SUCCESS, payload: res.data})
                        })
                        .catch(function(err) {
                            dispatch({type: atypes.CREATE_OCI_INSTANCES_FAILURE, payload: err});
                        });
                }
                //dispatch({type: atypes.LOAD_OCI_INSTANCES_SUCCESS, payload: result.data});

            })
            .catch(function(err) {
                dispatch({type: atypes.LOAD_OCI_INSTANCES_FAILURE, payload: err});
            });
    }
    catch (error) {
        dispatch({type: atypes.NO_TOKEN_FOUND, payload: error});
    }
};

export const redirectToTenant = (myWindow, auth, id, hostName, redirectRelPath, productType, appId, uiMode) => (dispatch) => {
    const invalidDomainError = {
        response: {
            data: {
                status: 401,
                errorMessage: 'Unauthorized: Access is denied.',
                errorCode: 1701,
                httpStatus: 401,
            }
        }
    };
    if (!_.isEmpty(hostName) && !verifyNetAppDomain(hostName)) {
        //Security issue, potential redirect to hacker site
        invalidDomainError.response.data.errorTitle = `Invalid Data Infrastructure Insights tenant: ${hostName}`;
        window.CIG_LOG.error('Invalid or unexpected URL: ' + hostName);
        dispatch({type: atypes.SECURITY_INVALID_DOMAIN, payload: invalidDomainError});
    }
    else {
        try {
            const headers = getAuthorizedHeader(auth);
            const URI = REST_API + ((productType === CS_PRODUCT) ? '/csecure/' : '/tenants/');
            const urlAuthorizationPath = '/authorize?uimode=true';
            axios
                .get(URI + id + urlAuthorizationPath, {headers})
                .then((res) => {
                    const tenantId = res.data.tenant_id;
                    const tenantToken = res.data.tenant_token;
                    const tenantUri = res.data.tenant_uri;
                    const expiresTime = res.data.expires_time;
                    uiMode = uiMode || res.data.uiMode;

                    let tenantName = tenantUri;
                    let relativePath = _.isEmpty(redirectRelPath) || _.isNil(redirectRelPath) ? '/web/#' : redirectRelPath;

                    if (appId === REPORTING_APP_ID) {
                        tenantName = 'reporting-' + tenantUri;
                        // redirect path of Reporting (DWH) is base64 encoded
                        const validateObj = validateEncodeRedirectPath(redirectRelPath);
                        if (validateObj === false) {
                            invalidDomainError.response.data.errorTitle = 'Invalid Data Infrastructure Insights Redirect Path: ';
                            invalidDomainError.response.data.errorCode = 1016;
                            const errMsg = 'Invalid reporting relative path: ' + relativePath + ' tenant: ' + tenantName
                            window.CIG_LOG.error('Invalid Data Infrastructure Insights tenant redirect path');
                            const props = {
                                ...invalidDomainError,
                                errorMessage: errMsg,
                                logPrefix: 'redirectToTenant - ',
                                tenantId: tenantId,
                            }
                            useServerLog(props);
                            dispatch({type: atypes.SECURITY_INVALID_DOMAIN, payload: invalidDomainError});
                        }
                    }
                    else {
                        relativePath = updateQueryStringParam(relativePath, OCI_SSO.webuiToken, expiresTime);
                        const mode = getQueryStringParam(UI_MODE.KEY);
                        if (_.isEmpty(mode)) {
                            if (!_.isEmpty(uiMode)) {
                                relativePath = productType === CS_PRODUCT ?
                                    updateQueryStringParam(relativePath, UI_MODE.KEY, CS_PRODUCT) :
                                    updateQueryStringParam(relativePath, UI_MODE.KEY, uiMode);
                            }
                        }
                        else if (validateUiMode(mode)) {
                            relativePath = updateQueryStringParam(relativePath, UI_MODE.KEY, _.toUpper(mode));
                            if (_.includes(relativePath, UI_MODE.KEY + '=CI')) {
                                // This is work around for Webui, since it accepts mode=CM for CI tenants
                                // It should take mode=CI like it does for mode=CS and mode=ONTAP.
                                relativePath = updateQueryStringParam(relativePath, UI_MODE.KEY, UI_MODE.CM_MODE);
                            }
                        }
                    }

                    // Check if user provides a tenant name that matches tenant name from Gateway.
                    if (!_.isEmpty(hostName) && _.trim(hostName) !== tenantName) {
                        //Security issue, potential redirect to hacker site
                        invalidDomainError.response.data.errorTitle = `Invalid Data Infrastructure Insights tenant: ${hostName}`;
                        const errMsg = 'Invalid or unexpected URL. Actual tenant ' + tenantName + ' is not the same as redirect tenant ' + hostName;
                        window.CIG_LOG.error(`Invalid Data Infrastructure Insights tenant: ${hostName}`);
                        const props = {
                            ...invalidDomainError,
                            errorMessage: errMsg,
                            logPrefix: 'redirectToTenant - ',
                            tenantId: tenantId,
                        }
                        useServerLog(props);
                        dispatch({type: atypes.SECURITY_INVALID_DOMAIN, payload: invalidDomainError});
                    }
                    else {
                        ociConnection(myWindow, tenantName, tenantToken, relativePath, appId);
                        dispatch({type: atypes.CREATE_OCI_USER_TOKEN_SUCCESS, payload: res.data});
                    }

                })
                .catch(function (err) {
                    dispatch({type: atypes.CREATE_OCI_USER_TOKEN_FAILURE, payload: err});
                });
        }
        catch (error) {
            dispatch({type: atypes.CREATE_OCI_USER_TOKEN_FAILURE, payload: error});
        }
    }
};

export const clearError = () => {
    return ({type: atypes.CLEAR_OCI_ERROR});
};

export const renewTokens = (numberOfTried) => (dispatch) => {
    try {
        dispatch({type: atypes.RENEW_TOKENS_REQUEST});
        axios
            .post('/ui/renew', {}, { withCredentials: true })
            .then(() => {
                dispatch({type: atypes.RENEW_TOKENS_SUCCESS});
            })
            .catch(function (err) {
                if (err.response.status === 429 && numberOfTried < 1) {
                    ++numberOfTried;
                    dispatch({type: atypes.RENEW_TOKENS_RETRY, payload: numberOfTried});
                }
                else {
                    window.CIG_LOG.debug('Failed to renew access token: ' + err);
                    dispatch({type: atypes.RENEW_TOKENS_FAILURE, payload: err});
                }
            });
    }
    catch (error) {
        dispatch({type: atypes.RENEW_TOKENS_FAILURE, payload: error});
    }
};
